// Simple and fast Perlin 2D noise implementation for procedural terrain height and biome mapping
class SimplexNoise {
  private p: number[];
  private perm: number[];
  private grad3 = [
    [1, 1, 0], [-1, 1, 0], [1, -1, 0], [-1, -1, 0],
    [1, 0, 1], [-1, 0, 1], [1, 0, -1], [-1, 0, -1],
    [0, 1, 1], [0, -1, 1], [0, 1, -1], [0, -1, -1]
  ];

  constructor(seed: number = 42) {
    this.p = [];
    for (let i = 0; i < 256; i++) {
      this.p[i] = Math.floor(this.pseudoRandom(seed + i) * 256);
    }
    this.perm = new Array(512);
    for (let i = 0; i < 512; i++) {
      this.perm[i] = this.p[i & 255]!;
    }
  }

  private pseudoRandom(seed: number) {
    const x = Math.sin(seed++) * 10000;
    return x - Math.floor(x);
  }

  private dot(g: number[], x: number, y: number) {
    return g[0]! * x + g[1]! * y;
  }

  public noise2D(xin: number, yin: number): number {
    const F2 = 0.5 * (Math.sqrt(3.0) - 1.0);
    const G2 = (3.0 - Math.sqrt(3.0)) / 6.0;

    let s = (xin + yin) * F2;
    let i = Math.floor(xin + s);
    let j = Math.floor(yin + s);
    let t = (i + j) * G2;
    let X0 = i - t;
    let Y0 = j - t;
    let x0 = xin - X0;
    let y0 = yin - Y0;

    let i1: number, j1: number;
    if (x0 > y0) {
      i1 = 1;
      j1 = 0;
    } else {
      i1 = 0;
      j1 = 1;
    }

    let x1 = x0 - i1 + G2;
    let y1 = y0 - j1 + G2;
    let x2 = x0 - 1.0 + 2.0 * G2;
    let y2 = y0 - 1.0 + 2.0 * G2;

    let ii = i & 255;
    let jj = j & 255;
    let gi0 = this.perm[ii + this.perm[jj]!]! % 12;
    let gi1 = this.perm[ii + i1 + this.perm[jj + j1]!]! % 12;
    let gi2 = this.perm[ii + 1 + this.perm[jj + 1]!]! % 12;

    let t0 = 0.5 - x0 * x0 - y0 * y0;
    let n0 = 0;
    if (t0 > 0) {
      t0 *= t0;
      n0 = t0 * t0 * this.dot(this.grad3[gi0]!, x0, y0);
    }

    let t1 = 0.5 - x1 * x1 - y1 * y1;
    let n1 = 0;
    if (t1 > 0) {
      t1 *= t1;
      n1 = t1 * t1 * this.dot(this.grad3[gi1]!, x1, y1);
    }

    let t2 = 0.5 - x2 * x2 - y2 * y2;
    let n2 = 0;
    if (t2 > 0) {
      t2 *= t2;
      n2 = t2 * t2 * this.dot(this.grad3[gi2]!, x2, y2);
    }

    // Returns scale approx -1 to 1, convert to 0..1
    return 70.0 * (n0 + n1 + n2);
  }
}

export function createNoiseGenerator(seed: number = 42) {
  const noise = new SimplexNoise(seed);
  return {
    get2D: (x: number, z: number, scale: number = 0.05): number => {
      return (noise.noise2D(x * scale, z * scale) + 1) / 2;
    },
    getOctave: (x: number, z: number, octaves: number = 3, persistence: number = 0.5): number => {
      let total = 0;
      let frequency = 0.03;
      let amplitude = 1;
      let maxValue = 0;
      for (let i = 0; i < octaves; i++) {
        total += (noise.noise2D(x * frequency, z * frequency) + 1) / 2 * amplitude;
        maxValue += amplitude;
        amplitude *= persistence;
        frequency *= 2;
      }
      return total / maxValue;
    }
  };
}
